import dayjs from '../../utils/dayjs';
import { Text, Modal, InlineStack, BlockStack, Grid, Form, FormLayout, TextField, Button, ChoiceList, Divider, EmptyState } from '@shopify/polaris';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import helpers from 'helpers/index';
import { ENUM_ORDER_PAYMENT_STATUS, useGetOrder, useMutateOrderPaid } from 'queries/orders.query';
import { useNotification } from 'context/NotificationContext';
import __ from 'languages/index';

import QuickSearchUser from 'components/quickSearchUser';
import { TypedUser } from 'interface/user.model';
import { ResponsivePopover } from 'components/ResponsivePopover';
import { ENUM_TRANSACTION_PAYMENT_METHOD, TypedTransactions, useMutateDeleteTransaction, useMutateGetQRImage } from 'queries/transactions.query';
// import { ENUM_ORDERS_PAYMENT_STATUS } from 'queries/trip_manifest.query';
import { Player } from '@lottiefiles/react-lottie-player';
import OnlinePayment from 'media/lottie_files/Online Payment.json';
import QRScan from 'media/lottie_files/QR Scan.json';
import { useField, useForm } from '@shopify/react-form';
import Capacity from 'components/Capacity';

export default function PaymentOrderModal({ show, order_id, onClose }: { show: boolean; order_id: string; onClose: (reload: boolean) => void }) {
  const { addNotification } = useNotification();
  const { data: orderData, refetch: refetchOrderData, isPending: loadingGetTicketViaOrderCode } = useGetOrder(order_id);
  const { mutateAsync: paidForThisOrder } = useMutateOrderPaid();
  const { mutateAsync: deleteTransaction, isPending: deletingTransaction } = useMutateDeleteTransaction();
  const [transactionWaitingForConfirmation, setTransactionWaitingForConfirmation] = useState<string>(null);

  const { mutateAsync: getQRPaymentImage, isPending: loadingQRCODE, isError: creatingQRMeetError } = useMutateGetQRImage();
  const [qrCodeImageRef, setQrCodeImageRef] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [hasDebitTransaction, setHasDebitTransaction] = useState(false);
  const [totalDebitTransaction, setTotalDebitTransaction] = useState(0);
  const qrCodeRequestRef = useRef(0);

  useEffect(() => {
    const allDebitTransaction: TypedTransactions[] =
      orderData?.transactions?.filter((t) =>
        [
          ENUM_TRANSACTION_PAYMENT_METHOD.DEBT_AGENT,
          ENUM_TRANSACTION_PAYMENT_METHOD.DEBT_CUSTOMER,
          ENUM_TRANSACTION_PAYMENT_METHOD.THIRD_PARTY,
        ].includes(t.payment_method)
      ) ?? [];

    const hasDebitTransaction = !!(orderData?.transactions && allDebitTransaction.length > 0);
    setHasDebitTransaction(hasDebitTransaction);
    const totalDebitAmount = allDebitTransaction.reduce((total, t) => total + Number(t.amount ?? 0), 0);
    setTotalDebitTransaction(totalDebitAmount);
  }, [orderData]);

  const recentTransactions = useMemo(
    () => [...(orderData?.transactions || [])].sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0)),
    [orderData?.transactions]
  );

  const generateQRCodeForPaymentCallback = useCallback(
    async (amount: number) => {
      const requestId = ++qrCodeRequestRef.current;
      setQrCodeImageRef(null);
      try {
        const QRCodeData = await getQRPaymentImage({ order_id, amount });
        if (requestId !== qrCodeRequestRef.current) return;

        // Hiển thị QR code trong một popover
        setQrCodeImageRef({
          qr_code_data: QRCodeData.qr_code_data,
          timestamp: QRCodeData.timestamp,
        });
      } catch (e) {
        // addNotification('error', 'Không thể tạo QR code cho đơn hàng này.');
      }
    },
    [order_id, getQRPaymentImage]
  );

  /** Ai thu tiền? */
  const [popoverActive, setPopoverActive] = useState(false);
  const useFields = {
    /** create new one */
    casher: useField<string>({ value: '', validates: [] }),
    casher_name: useField<string>({ value: '', validates: [] }),
    payment_method: useField<string>({ value: 'CASH', validates: [] }),
    description: useField<string>({ value: '', validates: [] }),
    amount: useField<string>({ value: '', validates: [(value) => (Number(value) >= 0 ? undefined : 'Số tiền phải lớn hơn 0')] }),
  };

  const { fields, submit, submitting, dirty, makeClean, reset } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        await paidForThisOrder({
          order_id: BigInt(order_id),
          casher: values.casher ? BigInt(values.casher) : undefined,
          payment_method: values.payment_method,
          description: values.description,
          amount: Number(values.amount),
        });

        onClose(true);
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success', errors: [] };
    },
  });
  const paymentAmount = useFields.amount.value;

  useEffect(() => {
    if (!show) {
      qrCodeRequestRef.current += 1;
      setQrCodeImageRef(null);
      setShowQRCode(false);
      helpers.resetForm(useFields);
    } else {
      refetchOrderData();
      setTransactionWaitingForConfirmation(null);
    }
  }, [show]);

  useEffect(() => {
    if (!show || !showQRCode || !orderData) return;

    const enteredAmount = paymentAmount.trim();
    const qrCodeAmount = enteredAmount === '' ? 0 : Number(enteredAmount);
    if (!Number.isFinite(qrCodeAmount) || qrCodeAmount < 0) return;

    const timeout = window.setTimeout(() => generateQRCodeForPaymentCallback(qrCodeAmount), enteredAmount === '' ? 0 : 500);

    return () => window.clearTimeout(timeout);
  }, [generateQRCodeForPaymentCallback, orderData?.id, paymentAmount, show, showQRCode]);

  useEffect(() => {
    if (!show) return;
    if (!orderData) return;
    if (orderData?.payment_status === ENUM_ORDER_PAYMENT_STATUS.PAID && !hasDebitTransaction) {
      setShowQRCode(false);
    } else {
      setShowQRCode(true);
    }
  }, [orderData, show, hasDebitTransaction]);

  const deleteTransactionCallback = useCallback(
    async (transactionId: string) => {
      if (transactionWaitingForConfirmation !== transactionId) {
        setTransactionWaitingForConfirmation(transactionId);
        return;
      }

      try {
        await deleteTransaction(transactionId);
        setTransactionWaitingForConfirmation(null);
        await refetchOrderData();
        addNotification('info', 'Đã xóa thanh toán.');
      } catch (e) {
        addNotification('error', 'Không thể xóa thanh toán này.');
      }
    },
    [addNotification, deleteTransaction, refetchOrderData, transactionWaitingForConfirmation]
  );

  return (
    <>
      <Modal
        size="large"
        open={show}
        title={`Thanh toán đơn hàng ${orderData?.order_code} ID #${orderData?.id} - ${orderData?.customer_data ? `${orderData?.customer_data?.display_name} - ${orderData?.customer_data?.user_phonenumber}` : 'Chưa có thông tin khách hàng'}`}
        onClose={() => onClose(false)}
      >
        <Modal.Section>
          <Form onSubmit={() => {}}>
            <Grid columns={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}>
              <Grid.Cell>
                <BlockStack gap="400">
                  <FormLayout.Group>
                    <TextField readOnly autoComplete="off" label="Khách hàng" value={orderData?.customer_data?.display_name} />
                    <TextField readOnly autoComplete="off" label="Số điện thoại" value={orderData?.customer_data?.user_phonenumber} />
                  </FormLayout.Group>
                  <FormLayout>
                    <TextField readOnly autoComplete="off" label="Điểm đi" value={orderData?.pickup_address} />
                    <TextField readOnly autoComplete="off" label="Điểm đến" value={orderData?.dropoff_address} />
                  </FormLayout>
                  <Divider />
                  <Text as="h3" variant="headingMd">
                    Thanh toán gần đây ( {helpers.formatNumber(orderData?.total_paid || 0)} / {helpers.formatNumber(orderData?.total_must_pay || 0)} )
                  </Text>
                  {recentTransactions.length ? (
                    recentTransactions.map((transaction, index) => (
                      <BlockStack gap="100" key={transaction.id || index}>
                        <InlineStack align="space-between" blockAlign="center">
                          <p>
                            <Text as="span" fontWeight="bold" variant="headingLg">
                              {helpers.formatNumber(transaction.amount)} VND
                            </Text>{' '}
                            / <strong>{transaction.payment_method}</strong>
                          </p>
                          <p>{dayjs(new Date(Number(transaction.createdAt))).format('HH:mm DD/MM/YYYY')}</p>
                        </InlineStack>
                        <Text as="p">
                          Thu bởi{' '}
                          <strong>
                            {transaction.casher_data?.display_name || '--'} / {transaction.casher_data?.user_phonenumber || '--'}
                          </strong>
                        </Text>
                        <Text as="p">Nội dung: {transaction.description || '--'}</Text>
                        {transaction.payment_method !== ENUM_TRANSACTION_PAYMENT_METHOD.BANK && transaction.id ? (
                          <Capacity current_user_can="manage_transaction">
                            <Button
                              tone="critical"
                              variant="primary"
                              loading={deletingTransaction && transactionWaitingForConfirmation === transaction.id}
                              disabled={deletingTransaction}
                              onClick={() => deleteTransactionCallback(transaction.id)}
                            >
                              {transactionWaitingForConfirmation === transaction.id ? 'Có chắc không?' : 'Xóa thanh toán'}
                            </Button>
                          </Capacity>
                        ) : null}
                        {index < recentTransactions.length - 1 ? <Divider /> : null}
                      </BlockStack>
                    ))
                  ) : (
                    <Text as="p" tone="subdued">
                      Chưa có thanh toán nào.
                    </Text>
                  )}
                </BlockStack>
              </Grid.Cell>
              <Grid.Cell>
                <div>
                  <Text as="p">
                    <Text as="span" variant="headingLg">
                      {helpers.getPad(orderData?.total_seat)}
                    </Text>{' '}
                    vé /{' '}
                    <Text as="span" variant="headingLg">
                      {helpers.formatNumber(orderData?.total_must_pay || 0)} vnd
                    </Text>
                  </Text>
                </div>

                <br />

                {showQRCode ? (
                  <BlockStack inlineAlign="center" gap="200">
                    {loadingQRCODE ? (
                      <div style={{ width: '260px', height: '160px' }}>
                        <Player src={QRScan} autoplay loop />
                      </div>
                    ) : null}
                    {qrCodeImageRef ? (
                      <a href={qrCodeImageRef.qr_code_data} download={`qrcode_payment_${order_id}.png`} target="_blank" rel="noopener noreferrer">
                        <img src={qrCodeImageRef.qr_code_data} alt="QR Code" style={{ width: '200px', height: 'auto' }} />
                      </a>
                    ) : null}
                    {creatingQRMeetError ? <EmptyState image="" heading="KHÔNG TẠO ĐƯỢC MÃ QR!" /> : null}
                    <Text as="p">
                      Chuyển khoản theo nội dung: <i>TT ve {orderData?.order_code}</i>
                    </Text>
                  </BlockStack>
                ) : null}

                <br />

                {orderData?.payment_status === ENUM_ORDER_PAYMENT_STATUS.PAID && !hasDebitTransaction ? (
                  <BlockStack gap="400" inlineAlign="center">
                    <>
                      <div style={{ width: '160px', height: '160px' }}>
                        <Player autoplay src={OnlinePayment} loop={true} />
                      </div>
                      <Text as="h3" variant="headingMd">
                        Vé đã được thanh toán đủ!
                      </Text>
                    </>
                  </BlockStack>
                ) : null}

                {hasDebitTransaction ? (
                  <Text as="h3" alignment="center" tone="subdued">
                    Đơn hàng này cần được gạt nợ số tiền {helpers.formatNumberWithCommas(totalDebitTransaction)} vnd
                  </Text>
                ) : null}

                {orderData?.payment_status !== ENUM_ORDER_PAYMENT_STATUS.PAID ? (
                  <BlockStack gap="400">
                    <TextField
                      label="Số tiền thanh toán"
                      type="number"
                      autoComplete="off"
                      suffix="VND"
                      labelAction={{
                        onAction: () => {
                          let remainValue = Math.max(0, Number(orderData?.total_must_pay || 0) - Number(orderData?.total_paid || 0)).toString();
                          useFields.amount.onChange(remainValue);
                        },
                        content: 'Số còn lại',
                      }}
                      helpText={`Còn lại ${helpers.formatNumber(Math.max(0, Number(orderData?.total_must_pay || 0) - Number(orderData?.total_paid || 0)))} VND`}
                      {...useFields.amount}
                    />
                    <ChoiceList
                      title="Hình thức thanh toán"
                      choices={[
                        {
                          label: 'Tiền mặt',
                          value: 'CASH',
                        },
                        {
                          label: 'Chuyển khoản ngân hàng',
                          value: 'BANK',
                          helpText: 'Chỉ được phép chuyển vào tài khoản công ty.',
                        },
                        {
                          label: 'Công nợ khách hàng',
                          value: 'DEBT_CUSTOMER',
                          helpText: 'Khách lẻ nợ.',
                        },
                        {
                          label: 'Công nợ đại lý',
                          value: 'DEBT_AGENT',
                          helpText: 'Dành cho tài khoản công nợ đại lý.',
                        },
                        {
                          label: 'Công nợ bên thứ ba',
                          value: 'THIRD_PARTY',
                          helpText: 'Bên thứ ba như Vé xe rẻ, An Vui.',
                        },
                      ]}
                      selected={[useFields.payment_method.value]}
                      onChange={(value) => useFields.payment_method.onChange(value.pop())}
                    />

                    <TextField autoComplete="off" label="Ghi chú" multiline={2} {...useFields.description} />

                    <ResponsivePopover
                      title="Ai thu tiền cho đơn này" // Title cho Modal trên di động
                      preferredPosition="below"
                      autofocusTarget="first-node"
                      active={popoverActive}
                      activator={
                        <InlineStack gap="400">
                          <Text as="p">Đã thu tiền bởi: </Text>
                          <Button disclosure variant="plain" onClick={() => setPopoverActive(!popoverActive)}>
                            {useFields.casher_name.value || 'Chưa có ai, click để thêm!'}
                          </Button>
                        </InlineStack>
                      }
                      onClose={() => setPopoverActive(false)}
                    >
                      <QuickSearchUser
                        title={''}
                        includeMe
                        user_role="driver,staff,sale,sale_admin,admin,super_admin"
                        onClose={(u: TypedUser) => {
                          useFields.casher.onChange(u.user_id);
                          useFields.casher_name.onChange(u.display_name);
                          setPopoverActive(false);
                        }}
                      />
                    </ResponsivePopover>

                    <Button tone="success" variant="primary" onClick={submit} loading={submitting} disabled={submitting}>
                      Thanh toán
                    </Button>
                  </BlockStack>
                ) : null}
              </Grid.Cell>
            </Grid>
          </Form>
        </Modal.Section>
      </Modal>
    </>
  );
}
